if (typeof BundleBuilder !== 'function') {
  class BundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onCartUpdated = this.onCartUpdated.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.scheduleLayoutUpdate = this.scheduleLayoutUpdate.bind(this);
    }

    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
      this.filterButtons = Array.from(this.querySelectorAll('[data-bundle-filter-button]'));
      this.progressSteps = Array.from(this.querySelectorAll('[data-bundle-progress-step]'));
      this.summary = this.querySelector('.bundle-builder__summary');
      this.progress = this.querySelector('.bundle-builder__progress');
      this.progressLine = this.querySelector('.bundle-builder__progress-line');
      this.progressFill = this.querySelector('[data-bundle-progress-fill]');
      this.productsWrap = this.querySelector('[data-bundle-products-wrap]');
      this.productsScroll = this.querySelector('[data-bundle-products-scroll]');
      this.summaryItems = this.querySelector('[data-bundle-summary-items]');
      this.summaryTotal = this.querySelector('[data-bundle-summary-total]');
      this.rewardsProgress = this.querySelector('[data-bundle-rewards]');
      this.rewardSteps = Array.from(this.querySelectorAll('[data-bundle-reward]'));
      this.cartRewardsSubtotal = Number(this.rewardsProgress?.dataset.cartSubtotal) || 0;
      this.cartHasGift = this.rewardsProgress?.dataset.giftInCart === 'true';
      this.giftPicker = this.querySelector('[data-bundle-gift-picker]');
      this.selectedGift = null;
      this.giftUnlocked = false;
      this.summaryCounts = Array.from(this.querySelectorAll('[data-bundle-summary-count]'));
      this.summaryMessage = this.querySelector('[data-bundle-summary-message]');
      this.progressMessage = this.querySelector('[data-bundle-progress-message]');
      this.summaryCartHint = this.querySelector('[data-bundle-summary-cart-hint]');
      this.summaryToggle = this.querySelector('[data-bundle-summary-toggle]');
      this.summaryWrap = this.querySelector('.bundle-builder__summary-wrap');
      this.summaryAnchor = this.querySelector('.bundle-builder__summary-anchor');
      this.addToCartButton = this.querySelector('[data-bundle-add-to-cart]');
      this.maxSummarySlots = 5;
      this.feedbackDuration = 2500;
      this.removedFeedbackItems = [];
      this.pendingCartRemovalKeys = new Set();
      this.isSummaryProcessing = false;

      this.eligibleHandles = this.readEligibleHandles();
      this.cartEligibleCount = 0;
      this.cartEligibleItems = [];
      this.cartEligibleSubtotal = 0;
      this.cartEligibleOriginalSubtotal = 0;

      this.thresholds = window.KROWN?.volumePricing?.parseTiers([
        {
          qty: parseInt(this.dataset.thresholdOne || '3', 10),
          discount: parseInt(this.dataset.discountOne || '15', 10),
        },
        {
          qty: parseInt(this.dataset.thresholdTwo || '4', 10),
          discount: parseInt(this.dataset.discountTwo || '20', 10),
        },
        {
          qty: parseInt(this.dataset.thresholdThree || '5', 10),
          discount: parseInt(this.dataset.discountThree || '25', 10),
        },
      ]) ?? [
        {
          qty: parseInt(this.dataset.thresholdOne || '3', 10),
          discount: parseInt(this.dataset.discountOne || '15', 10),
        },
        {
          qty: parseInt(this.dataset.thresholdTwo || '4', 10),
          discount: parseInt(this.dataset.discountTwo || '20', 10),
        },
        {
          qty: parseInt(this.dataset.thresholdThree || '5', 10),
          discount: parseInt(this.dataset.discountThree || '25', 10),
        },
      ];

      const activeButton = this.filterButtons.find((button) => button.classList.contains('is-active')) || this.filterButtons[0];
      this.activeFilter = activeButton?.dataset.filter || '';
      if (activeButton && !activeButton.classList.contains('is-active')) {
        activeButton.classList.add('is-active');
      }

      this.bindFilters();
      this.bindCards();
      this.bindSummary();

      this.applyActiveFilter();

      requestAnimationFrame(() => {
        this.cards.forEach((card) => this.syncCard(card));
        this.update();
        this.scheduleLayoutUpdate();
      });

      if (this.eligibleHandles.size > 0 || this.rewardsProgress) {
        this.refreshCartEligibleCount().then(() => this.update());
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.scheduleLayoutUpdate();
        });

        this.cards.forEach((card) => this.resizeObserver.observe(card));
      }

      window.addEventListener('resize', this.onResize);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('load', this.scheduleLayoutUpdate);
      this.onScroll();
      document.addEventListener('cart-updated', this.onCartUpdated);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.ajaxCartForm = document.getElementById('AjaxCartForm');
      if (this.ajaxCartForm) {
        this.ajaxCartForm.addEventListener('cart-updated', this.onCartUpdated);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('load', this.scheduleLayoutUpdate);
      window.cancelAnimationFrame(this.progressStickyFrame);
      this.progressSentinel?.remove();
      document.removeEventListener('cart-updated', this.onCartUpdated);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.ajaxCartForm?.removeEventListener('cart-updated', this.onCartUpdated);
      this.resizeObserver?.disconnect();
    }

    onVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      this.onCartUpdated();
    }

    onCartUpdated() {
      if (!this.eligibleHandles || (this.eligibleHandles.size === 0 && !this.rewardsProgress)) return;
      this.refreshCartEligibleCount().then(() => this.update());
    }

    onResize() {
      this.scheduleLayoutUpdate();
      this.onScroll();
    }

    onScroll() {
      if (!this.progress) return;

      window.cancelAnimationFrame(this.progressStickyFrame);
      this.progressStickyFrame = window.requestAnimationFrame(() => {
        if (!this.progressSentinel?.isConnected) {
          this.progressSentinel = document.createElement('span');
          this.progressSentinel.className = 'bundle-builder__progress-sentinel';
          this.progressSentinel.setAttribute('aria-hidden', 'true');
          this.progress.before(this.progressSentinel);
        }

        const stickyTop = parseFloat(window.getComputedStyle(this.progress).top) || 0;
        const isStuck = this.progressSentinel.getBoundingClientRect().top <= stickyTop;
        this.progress.classList.toggle('is-stuck', isStuck);
      });
    }

    scheduleLayoutUpdate() {
      window.cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = window.requestAnimationFrame(() => {
        this.updateProductsClamp();
      });
    }

    onDocumentClick(event) {
      if (window.innerWidth >= 1024) return;
      if (!this.classList.contains('is-summary-open')) return;

      const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
      if (eventPath.includes(this.summaryWrap) || this.summaryWrap?.contains(event.target)) return;

      this.classList.remove('is-summary-open');
    }

    bindFilters() {
      this.filterButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.activeFilter = button.dataset.filter;
          this.filterButtons.forEach((item) => item.classList.toggle('is-active', item === button));
          this.applyActiveFilter();
          this.scheduleLayoutUpdate();
        });
      });
    }

    applyActiveFilter() {
      if (!this.cards) return;
      this.cards.forEach((card) => {
        const filters = (card.dataset.filters || '').split('|').filter(Boolean);
        card.hidden = this.activeFilter ? !filters.includes(this.activeFilter) : false;
      });
    }

    bindCards() {
      this.querySelectorAll('[data-bundle-promo-add]').forEach((button) => {
        const card = this.cards.find((item) => item.dataset.productId === button.dataset.bundlePromoAdd);
        if (!card) {
          button.disabled = true;
          return;
        }
        button.addEventListener('click', () => this.changeQuantity(card, 1));
      });

      this.cards.forEach((card) => {
        card.querySelectorAll('[data-bundle-card-quick-view-trigger]').forEach((link) => {
          link.addEventListener('click', (event) => {
            event.preventDefault();
            card.querySelector('.bundle-builder__quick-view-button')?.click();
          });
        });

        card.querySelectorAll('[data-bundle-card-increase]').forEach((button) => {
          button.addEventListener('click', () => this.changeQuantity(card, 1));
          if (button.tagName !== 'BUTTON') {
            button.addEventListener('keydown', (event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              this.changeQuantity(card, 1);
            });
          }
        });

        card.querySelectorAll('[data-bundle-card-decrease]').forEach((button) => {
          button.addEventListener('click', () => this.changeQuantity(card, -1));
        });

        const variants = card.querySelector('product-variants');
        if (variants) {
          variants.addEventListener('VARIANT_CHANGE', this.onVariantChange);
          variants.addEventListener('change', async (event) => {
            if (customElements.get('product-variants')) return;
            await customElements.whenDefined('product-variants');
            if (variants.isConnected) variants.onOptionChange(event);
          });
          requestAnimationFrame(() => {
            const variant = variants.currentVariant || this.getVariant(card);
            if (variant) {
              this.updateCardImage(card, variant, { animate: false });
            }
          });
        }
      });
    }

    bindSummary() {
      this.summaryToggle?.addEventListener('click', () => {
        if (window.innerWidth < 1024) {
          this.classList.toggle('is-summary-open');
        }
      });

      this.summaryItems?.addEventListener('click', (event) => {
        if (this.isSummaryProcessing) return;

        if (event.target.closest('[data-bundle-remove-gift]')) {
          this.selectedGift = null;
          this.update();
          return;
        }

        const cartRemoveButton = event.target.closest('[data-bundle-remove-cart-item]');
        if (cartRemoveButton) {
          event.stopPropagation();
          const itemKey = cartRemoveButton.dataset.bundleRemoveCartItem;
          const feedbackId = this.showRemovedFeedback(cartRemoveButton, { persist: true, key: itemKey });
          this.removeCartItemFromCart(cartRemoveButton.dataset.bundleRemoveCartItem, cartRemoveButton, feedbackId);
          return;
        }

        const removeButton = event.target.closest('[data-bundle-remove-card]');
        if (!removeButton) return;

        event.stopPropagation();

        const card = this.querySelector(`[data-card-id="${removeButton.dataset.bundleRemoveCard}"]`);
        if (!card) return;

        const variantId = parseInt(removeButton.dataset.bundleRemoveVariant || '0', 10);
        if (variantId) {
          this.showRemovedFeedback(removeButton);
          this.setVariantQuantity(card, variantId, 0);
        }
        this.updateCardQuantityState(card);
        this.update();
      });

      this.addToCartButton?.addEventListener('click', () => {
        if (this.isSummaryProcessing) return;
        this.addBundleToCart();
      });
    }

    onVariantChange(event) {
      const card = event.currentTarget.closest('[data-bundle-card]');
      if (!card) return;

      requestAnimationFrame(() => {
        const variant = event.target.currentVariant || this.getVariant(card);
        card._bundleVariant = variant;
        this.updateCardImage(card, variant);
        this.updateCardQuantityState(card);
        this.update();
      });
    }

    updateCardImage(card, variant, { animate = true } = {}) {
      if (!card || !variant?.id) return;

      const variantData = this.getVariantById(card, variant.id);
      const imageSrc = variantData?.summary_image_src || variantData?.featured_image_src || '';
      if (!imageSrc) return;

      const mediaLink = card.querySelector('[data-js-product-item-image]');
      if (!mediaLink) return;

      const figures = [...mediaLink.querySelectorAll('figure')];
      const figure = figures[0];
      if (!figure) return;

      figures.slice(1).forEach((extraFigure) => extraFigure.remove());

      const img = figure.querySelector('img');
      if (!img) return;

      const normalizedSrc = imageSrc.split('?')[0];
      const currentSrc = (img.currentSrc || img.src || '').split('?')[0];
      if (currentSrc === normalizedSrc) return;

      if (!animate) {
        img.style.opacity = '';
        img.style.transform = '';
        img.src = imageSrc;
        img.srcset = `${imageSrc} 320w`;
        img.sizes = '(max-width: 1023px) 156px, 286px';
        figure.classList.remove('lazy-image--to-reveal');
        figure.classList.add('lazy-image--revealed', 'lazyloaded');
        return;
      }

      this.revealBundleCardImage(figure, imageSrc, img);
    }

    revealBundleCardImage(figure, imageSrc, img) {
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

      figure.classList.remove('lazy-image--revealed', 'product-item__image-figure--on-top');
      figure.classList.add('lazy-image--to-reveal');

      const finishReveal = () => {
        window.setTimeout(() => {
          figure.classList.remove('lazy-image--to-reveal');
          figure.classList.add('lazy-image--revealed', 'lazyloaded');
          img.style.opacity = '';
          img.style.transform = '';
        }, 50);
      };

      const onLoad = () => {
        img.removeEventListener('load', onLoad);
        img.removeEventListener('error', onLoad);
        finishReveal();
      };

      img.addEventListener('load', onLoad);
      img.addEventListener('error', onLoad);

      img.style.opacity = '0';
      if (isDesktop) {
        img.style.transform = 'translateY(20px) scale(1.05)';
      }

      img.src = imageSrc;
      img.srcset = `${imageSrc} 320w`;
      img.sizes = '(max-width: 1023px) 156px, 286px';

      if (img.complete && img.naturalWidth > 0) {
        onLoad();
      }
    }

    getCardVariantsData(card) {
      if (card._bundleVariantsData) {
        return card._bundleVariantsData;
      }

      const json = card.querySelector('[data-bundle-card-variants]');
      if (!json) return [];

      try {
        card._bundleVariantsData = JSON.parse(json.textContent);
      } catch (error) {
        card._bundleVariantsData = [];
      }

      return card._bundleVariantsData;
    }

    getVariant(card) {
      if (card._bundleVariant) {
        return card._bundleVariant;
      }

      const productVariants = card.querySelector('product-variants');
      if (productVariants?.currentVariant) {
        return productVariants.currentVariant;
      }

      const variantData = card.querySelector('[data-js-variant-data]');
      if (variantData) {
        try {
          return JSON.parse(variantData.textContent);
        } catch (error) {
          return null;
        }
      }

      const variantIdInput = card.querySelector('.product-form input[name="id"]');
      if (variantIdInput?.value) {
        return {
          id: parseInt(variantIdInput.value, 10),
          available: !card.querySelector('.product-form .add-to-cart.disabled'),
        };
      }

      return null;
    }

    getVariantById(card, variantId) {
      if (!variantId) return null;

      const normalizedVariantId = String(variantId);
      const variantData = this.getCardVariantsData(card).find((variant) => String(variant.id) === normalizedVariantId) || null;
      const currentVariant = this.getVariant(card);
      if (currentVariant && String(currentVariant.id) === normalizedVariantId) {
        return { ...variantData, ...currentVariant };
      }

      return variantData;
    }

    getCardSelections(card) {
      if (!card._bundleSelections) {
        card._bundleSelections = {};
      }

      return card._bundleSelections;
    }

    getCurrentVariantQuantity(card) {
      const variant = this.getVariant(card);
      if (!variant?.id) return 0;

      return this.getVariantQuantity(card, variant.id);
    }

    getVariantQuantity(card, variantId) {
      if (!variantId) return 0;

      const selections = this.getCardSelections(card);
      return parseInt(selections[String(variantId)] || '0', 10);
    }

    setVariantQuantity(card, variantId, quantity) {
      if (!variantId) return;

      const selections = this.getCardSelections(card);
      const normalizedVariantId = String(variantId);
      const nextQuantity = Math.max(0, quantity);

      if (nextQuantity > 0) {
        selections[normalizedVariantId] = nextQuantity;
      } else {
        delete selections[normalizedVariantId];
      }
    }

    syncCard(card) {
      card._bundleVariant = this.getVariant(card);
    }

    readEligibleHandles() {
      const script = this.querySelector('[data-bundle-eligible-handles]');
      if (!script) return new Set();

      try {
        const parsed = JSON.parse(script.textContent || '[]');
        if (Array.isArray(parsed)) {
          return new Set(parsed.map((handle) => String(handle).toLowerCase()));
        }
      } catch (error) {
        console.warn('BundleBuilder: failed to parse eligible handles', error);
      }

      return new Set();
    }

    async refreshCartEligibleCount() {
      if (!this.eligibleHandles || (this.eligibleHandles.size === 0 && !this.rewardsProgress)) {
        this.cartEligibleCount = 0;
        this.cartEligibleItems = [];
        this.cartEligibleSubtotal = 0;
        this.cartEligibleOriginalSubtotal = 0;
        this.cartRewardsSubtotal = 0;
        return 0;
      }

      try {
        const response = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(`cart.js ${response.status}`);
        const cart = await response.json();
        this.applyCartEligibleItems(cart);
      } catch (error) {
        console.warn('BundleBuilder: failed to read cart for eligible count', error);
        this.cartEligibleCount = 0;
        this.cartEligibleItems = [];
        this.cartEligibleSubtotal = 0;
        this.cartEligibleOriginalSubtotal = 0;
        this.cartRewardsSubtotal = 0;
      }

      return this.cartEligibleCount;
    }

    applyCartEligibleItems(cart) {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const removedFeedbackKeys = new Set(this.removedFeedbackItems.map((item) => item.key).filter(Boolean));
      const giftProductId = Number(this.rewardsProgress?.dataset.giftProductId) || 0;
      const giftLine = items.find((item) => giftProductId && Number(item.product_id) === giftProductId);
      this.cartGift = giftLine ? {
        key: giftLine.key,
        title: giftLine.variant_title || giftLine.product_title,
        image: giftLine.image || giftLine.featured_image?.url,
      } : null;
      this.cartHasGift = Boolean(giftLine);
      if (this.cartHasGift) this.selectedGift = null;
      this.cartRewardsSubtotal = items.reduce((sum, item) => {
        if (giftProductId && Number(item.product_id) === giftProductId) return sum;
        return sum + this.getCartItemLinePrice(item);
      }, 0);

      this.cartEligibleItems = items
        .filter((item) => {
          if (giftProductId && Number(item.product_id) === giftProductId) return false;
          const handle = String(item?.handle || '').toLowerCase();
          const key = String(item?.key || item?.variant_id || item?.id || '');
          return handle && this.eligibleHandles.has(handle) && !this.pendingCartRemovalKeys.has(key) && !removedFeedbackKeys.has(key);
        })
        .map((item) => ({
          id: String(item?.key || item?.variant_id || item?.id || ''),
          quantity: parseInt(item?.quantity || 0, 10),
          linePrice: this.getCartItemLinePrice(item),
          originalLinePrice: this.getCartItemOriginalLinePrice(item),
          title: String(item?.product_title || item?.title || ''),
          image: String(item?.image || item?.featured_image?.url || ''),
        }))
        .filter((item) => item.quantity > 0);

      this.cartEligibleCount = items.reduce((sum, item) => {
        if (giftProductId && Number(item.product_id) === giftProductId) return sum;
        const handle = String(item?.handle || '').toLowerCase();
        const key = String(item?.key || item?.variant_id || item?.id || '');
        if (key && (this.pendingCartRemovalKeys.has(key) || removedFeedbackKeys.has(key))) return sum;
        if (!handle || !this.eligibleHandles.has(handle)) return sum;
        return sum + parseInt(item?.quantity || 0, 10);
      }, 0);

      this.cartEligibleSubtotal = this.cartEligibleItems.reduce((sum, item) => sum + item.linePrice, 0);
      this.cartEligibleOriginalSubtotal = this.cartEligibleItems.reduce((sum, item) => sum + item.originalLinePrice, 0);

      return this.cartEligibleCount;
    }

    escapeAttribute(value) {
      return String(value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    getCartItemLinePrice(item) {
      const linePrice = item?.final_line_price ?? item?.line_price ?? item?.original_line_price;
      if (Number.isFinite(linePrice)) return parseInt(linePrice, 10);

      const unitPrice = parseInt(item?.final_price ?? item?.price ?? item?.original_price ?? 0, 10);
      const quantity = parseInt(item?.quantity ?? 0, 10);
      return unitPrice * quantity;
    }

    getCartItemOriginalLinePrice(item) {
      const linePrice = item?.original_line_price ?? item?.line_price ?? item?.final_line_price;
      if (Number.isFinite(linePrice)) return parseInt(linePrice, 10);

      const unitPrice = parseInt(item?.original_price ?? item?.price ?? item?.final_price ?? 0, 10);
      const quantity = parseInt(item?.quantity ?? 0, 10);
      return unitPrice * quantity;
    }

    changeQuantity(card, delta) {
      const variant = this.getVariant(card);
      if (!variant || variant.available === false) return;

      const currentQuantity = this.getVariantQuantity(card, variant.id);
      const nextQuantity = Math.max(0, currentQuantity + delta);
      const isIncreasingQuantity = delta > 0 && nextQuantity > currentQuantity;
      const isAddingNewItem = delta > 0 && currentQuantity === 0 && nextQuantity > 0;
      this.setVariantQuantity(card, variant.id, nextQuantity);
      this.updateCardQuantityState(card);
      this.update();

      if (isIncreasingQuantity) {
        this.showAddedFeedback(card);
      }

      if (isAddingNewItem) {
        this.bumpElement(this.summaryToggle || this.summaryWrap, 'is-bumping');
      }
    }

    showAddedFeedback(card) {
      if (!card) return;

      window.clearTimeout(card._bundleAddedFeedbackTimer);
      card.classList.remove('is-added-feedback');
      void card.offsetWidth;
      card.classList.add('is-added-feedback');

      card._bundleAddedFeedbackTimer = window.setTimeout(() => {
        card.classList.remove('is-added-feedback');
      }, this.feedbackDuration);
    }

    showRemovedFeedback(removeButton, options = {}) {
      if (!removeButton || !this.summaryItems) return null;

      const item = removeButton.closest('.bundle-builder__summary-item');
      if (!item) return null;

      const image = item.querySelector('img');
      const index = Array.from(this.summaryItems.children).indexOf(item);
      const feedbackItem = {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        index: Math.max(index, 0),
        key: options.key || '',
        image: image?.currentSrc || image?.getAttribute('src') || '',
        alt: image?.getAttribute('alt') || '',
      };

      this.removedFeedbackItems.push(feedbackItem);

      if (!options.persist) {
        window.setTimeout(() => {
          this.removeRemovedFeedback(feedbackItem.id);
        }, this.feedbackDuration);
      }

      return feedbackItem.id;
    }

    removeRemovedFeedback(feedbackId) {
      if (!feedbackId) return;
      this.removedFeedbackItems = this.removedFeedbackItems.filter((entry) => entry.id !== feedbackId);
      this.update();
    }

    updateCardQuantityState(card) {
      const quantity = this.getCurrentVariantQuantity(card);
      const totalQuantity = Object.values(this.getCardSelections(card)).reduce((sum, value) => {
        return sum + parseInt(value || '0', 10);
      }, 0);

      card.classList.toggle('is-selected', totalQuantity > 0);

      const count = card.querySelector('[data-bundle-card-count]');
      if (count) {
        count.textContent = String(quantity);
      }
    }

    getSelectedCards() {
      return this.cards.flatMap((card) => {
        const selections = this.getCardSelections(card);

        return Object.entries(selections)
          .map(([variantId, quantity]) => ({
            card,
            variantId,
            quantity: parseInt(quantity || '0', 10),
            variant: this.getVariantById(card, variantId),
          }))
          .filter((item) => item.quantity > 0 && item.variant);
      });
    }

    getDiscount(totalItems) {
      return window.KROWN?.volumePricing?.getDiscount(totalItems, this.thresholds) ?? 0;
    }

    getNextTier(totalItems) {
      return window.KROWN?.volumePricing?.getNextTier(totalItems, this.thresholds) ?? null;
    }

    updateProgress(totalItems) {
      this.progressSteps.forEach((step) => {
        const threshold = parseInt(step.dataset.threshold || '0', 10);
        step.classList.toggle('is-reached', totalItems >= threshold);
      });

      if (this.progressFill && this.progressLine && this.thresholds.length > 0) {
        this.progressFill.style.width = `${this.getProgressFillWidth(totalItems)}px`;
      }
    }

    getProgressFillWidth(totalItems) {
      const lineWidth = this.progressLine.offsetWidth || 0;
      if (!lineWidth) return 0;

      const getDotCenter = (step) => {
        const dot = step?.querySelector('.bundle-builder__progress-dot');
        if (!dot) return 0;
        return step.offsetLeft + dot.offsetLeft + dot.offsetWidth / 2 - this.progressLine.offsetLeft;
      };

      const firstThreshold = this.thresholds[0]?.qty || 0;
      const secondThreshold = this.thresholds[1]?.qty || firstThreshold;
      const thirdThreshold = this.thresholds[2]?.qty || secondThreshold;

      const firstCenter = getDotCenter(this.progressSteps[0]);
      const secondCenter = getDotCenter(this.progressSteps[1]);
      const thirdCenter = getDotCenter(this.progressSteps[2]);

      const lerp = (start, end, ratio) => start + (end - start) * ratio;
      const clampRatio = (value) => Math.min(Math.max(value, 0), 1);

      if (totalItems <= 0) {
        return 0;
      }

      if (totalItems <= firstThreshold) {
        const ratio = clampRatio(totalItems / Math.max(firstThreshold, 1));
        return lerp(0, firstCenter, ratio);
      }

      if (this.thresholds.length === 2 && totalItems >= secondThreshold) {
        return lineWidth;
      }

      if (totalItems <= secondThreshold) {
        const ratio = clampRatio((totalItems - firstThreshold) / Math.max(secondThreshold - firstThreshold, 1));
        return lerp(firstCenter, secondCenter, ratio);
      }

      if (totalItems < thirdThreshold) {
        const ratio = clampRatio((totalItems - secondThreshold) / Math.max(thirdThreshold - secondThreshold, 1));
        return lerp(secondCenter, thirdCenter, ratio);
      }

      return lineWidth;
    }

    renderSummaryItems(selectedCards) {
      if (!this.summaryItems) return;
      const removedFeedbackByKey = new Map(
        this.removedFeedbackItems.filter((item) => item.key).map((item) => [item.key, item])
      );

      const cartItemsMarkup = this.cartEligibleItems.map((item) => {
        const removedFeedback = removedFeedbackByKey.get(item.id);
        if (removedFeedback) {
          return this.renderRemovedSummaryItem(removedFeedback);
        }

        return `
          <div class="bundle-builder__summary-item" aria-label="Bundle item already in cart">
            <button type="button" class="bundle-builder__summary-item-remove" data-bundle-remove-cart-item="${this.escapeAttribute(item.id)}" aria-label="Remove cart item">×</button>
            ${item.quantity > 1 ? `<span class="bundle-builder__summary-item-qty">${item.quantity}</span>` : ''}
            <div class="bundle-builder__summary-item-media">
              ${item.image ? `<img src="${item.image}" alt="${item.title.replace(/"/g, '&quot;')}" loading="lazy">` : ''}
            </div>
          </div>
        `;
      });

      const selectedItemsMarkup = selectedCards.map(({ card, quantity, variantId }) => {
          const cardImage =
            card.querySelector('.bundle-builder__card-image--primary img') ||
            card.querySelector('.bundle-builder__card-image--single img') ||
            card.querySelector('.bundle-builder__card-media img');
          const variant = this.getVariantById(card, variantId);
          const imageSrc =
            variant?.summary_image_src ||
            variant?.featured_image_src ||
            cardImage?.currentSrc ||
            cardImage?.getAttribute('src') ||
            '';
          const imageAlt = cardImage?.getAttribute('alt') || card.dataset.productTitle || '';

          return `
            <div class="bundle-builder__summary-item">
              <button type="button" class="bundle-builder__summary-item-remove" data-bundle-remove-card="${card.dataset.cardId}" data-bundle-remove-variant="${variantId}" aria-label="Remove item">×</button>
              ${quantity > 1 ? `<span class="bundle-builder__summary-item-qty">${quantity}</span>` : ''}
              <div class="bundle-builder__summary-item-media">
                ${imageSrc ? `<img src="${imageSrc}" alt="${imageAlt.replace(/"/g, '&quot;')}" loading="lazy">` : ''}
              </div>
            </div>
          `;
        });

      const itemsMarkup = [...cartItemsMarkup, ...selectedItemsMarkup];
      const gift = this.giftUnlocked && (this.cartGift || this.selectedGift);
      if (gift) {
        const label = this.rewardsProgress.dataset.giftLabel;
        const image = gift.image || this.rewardsProgress.dataset.giftImage;
        itemsMarkup.push(`
          <div class="bundle-builder__summary-item bundle-builder__summary-item--gift" aria-label="${this.escapeAttribute(label)}: ${this.escapeAttribute(gift.title)}">
            <button type="button" class="bundle-builder__summary-item-remove"
              ${gift.key ? `data-bundle-remove-cart-item="${this.escapeAttribute(gift.key)}"` : 'data-bundle-remove-gift'}
              aria-label="Remove free gift">×</button>
            <div class="bundle-builder__summary-item-media">
              ${image ? `<img src="${this.escapeAttribute(image)}" alt="${this.escapeAttribute(gift.title)}" loading="lazy">` : ''}
            </div>
            <div class="bundle-builder__gift-badge">
              <span>${this.escapeAttribute(label)}</span>
              <span>${this.formatMoneyWithoutTrailingZeros(0)}</span>
            </div>
          </div>
        `);
      }
      this.removedFeedbackItems
        .filter((item) => !item.key)
        .sort((a, b) => a.index - b.index)
        .forEach((item, offset) => {
          itemsMarkup.splice(Math.min(item.index + offset, itemsMarkup.length), 0, this.renderRemovedSummaryItem(item));
        });

      this.summaryItems.innerHTML = itemsMarkup.join('');
    }

    renderRemovedSummaryItem(item) {
      return `
        <div class="bundle-builder__summary-item bundle-builder__summary-item--removed is-removed-feedback" aria-live="polite">
          <div class="bundle-builder__summary-item-media">
            ${item.image ? `<img src="${item.image}" alt="${item.alt.replace(/"/g, '&quot;')}" loading="lazy">` : ''}
          </div>
          <div class="bundle-builder__removed-overlay" aria-hidden="true">
            <span>Removed</span>
          </div>
        </div>
      `;
    }

    getBundleMessage(totalItems) {
      return window.KROWN?.volumePricing?.getTierMessage(totalItems, this.thresholds, {
        style: 'bundle',
        emptyLabel: this.dataset.labelSaveMore || 'Buy 3 & Save 15%',
        maxLabel: this.dataset.labelMaxSaved || '25% off unlocked',
      }) ?? (this.dataset.labelSaveMore || 'Buy 3 & Save 15%');
    }

    updateSummary(kitItems, combinedItems, subtotal, discount, cartSubtotal = 0, cartOriginalSubtotal = cartSubtotal) {
      const kitSubtotal = Math.max(subtotal - cartSubtotal, 0);
      const pricingTotals = window.KROWN?.volumePricing?.calculateSplitTotals({
        kitSubtotal,
        cartSubtotal,
        cartOriginalSubtotal,
        discountPercent: discount,
      }) ?? {
        currentTotal: subtotal,
        originalSubtotal: cartOriginalSubtotal + kitSubtotal,
        savings: 0,
        hasSavings: false,
      };
      const discountedTotal = pricingTotals.currentTotal;
      const originalSubtotal = pricingTotals.originalSubtotal;
      const savings = pricingTotals.savings;
      const hasVisibleItems = combinedItems > 0 || this.removedFeedbackItems.length > 0;

      if (this.summaryCounts) {
        this.summaryCounts.forEach((count) => {
          count.textContent = String(combinedItems);
        });
      }

      if (this.summaryToggle) {
        this.summaryToggle.classList.toggle('has-items', hasVisibleItems);
      }

      if (this.summaryWrap) {
        this.summaryWrap.classList.toggle('has-items', hasVisibleItems);
      }

      if (this.summary) {
        this.summary.classList.toggle('has-items', hasVisibleItems);
      }

      if (this.summaryTotal) {
        if (pricingTotals.hasSavings) {
          this.summaryTotal.innerHTML = `${this.formatMoneyWithoutTrailingZeros(discountedTotal)} <span class="bundle-builder__summary-total-compare">${this.formatMoneyWithoutTrailingZeros(originalSubtotal)}</span> <span class="bundle-builder__summary-total-save">(you save ${this.formatMoneyWithoutTrailingZeros(savings)})</span>`;
        } else {
          this.summaryTotal.textContent = this.formatMoneyWithoutTrailingZeros(subtotal);
        }
      }

      const bundleMessage = this.getBundleMessage(combinedItems);

      if (this.summaryMessage) {
        this.summaryMessage.textContent = bundleMessage;
      }

      if (this.progressMessage) {
        this.progressMessage.textContent = bundleMessage;
      }

      if (this.addToCartButton) {
        this.addToCartButton.disabled = this.isSummaryProcessing || kitItems === 0;
      }
    }

    update() {
      const selectedCards = this.getSelectedCards();
      const kitItems = selectedCards.reduce((sum, item) => sum + item.quantity, 0);
      const cartItems = this.cartEligibleCount || 0;
      const combinedItems = kitItems + cartItems;
      const kitSubtotal = selectedCards.reduce((sum, item) => sum + item.variant.price * item.quantity, 0);
      const cartSubtotal = this.cartEligibleSubtotal || 0;
      const cartOriginalSubtotal = this.cartEligibleOriginalSubtotal || cartSubtotal;
      const subtotal = kitSubtotal + cartSubtotal;
      const discount = this.getDiscount(combinedItems);

      this.cards.forEach((card) => this.updateCardQuantityState(card));
      this.updateProgress(combinedItems);
      this.updateSummary(kitItems, combinedItems, subtotal, discount, cartSubtotal, cartOriginalSubtotal);
      const giftProductId = Number(this.rewardsProgress?.dataset.giftProductId) || 0;
      const rewardsKitSubtotal = selectedCards.reduce((sum, item) => {
        if (giftProductId && Number(item.card.dataset.productId) === giftProductId) return sum;
        return sum + item.variant.price * item.quantity;
      }, 0);
      this.updateRewardsProgress(this.cartRewardsSubtotal + Math.round(rewardsKitSubtotal * (100 - discount) / 100));
      this.renderSummaryItems(selectedCards);
      this.updateProgressHint(cartItems);
      this.scheduleLayoutUpdate();
    }

    updateRewardsProgress(subtotal) {
      if (!this.rewardsProgress || !this.rewardSteps.length) return;
      const total = Math.max(0, subtotal);
      const lastThreshold = Number(this.rewardSteps[this.rewardSteps.length - 1].dataset.threshold);
      let fill = total >= lastThreshold ? 100 : 0;
      let previousThreshold = 0;
      let previousPosition = 0;

      this.rewardSteps.forEach((step, index) => {
        const threshold = Number(step.dataset.threshold);
        const position = (index + 0.5) / this.rewardSteps.length * 100;
        step.classList.toggle('is-unlocked', total >= threshold);
        step.setAttribute('aria-valuenow', Math.min(total, threshold));
        step.setAttribute('aria-valuetext', `${this.formatMoneyWithoutTrailingZeros(total)} / ${this.formatMoneyWithoutTrailingZeros(threshold)}`);
        if (total < lastThreshold && total >= previousThreshold && total <= threshold) {
          fill = previousPosition + (position - previousPosition) * (total - previousThreshold) / (threshold - previousThreshold);
        }
        previousThreshold = threshold;
        previousPosition = position;
      });
      this.rewardsProgress.querySelector('[data-bundle-rewards-fill]').style.width = `${fill}%`;
      this.updateGiftPicker(total);
    }

    updateGiftPicker(total) {
      if (!this.giftPicker) return;
      const threshold = Number(this.rewardSteps[this.rewardSteps.length - 1].dataset.threshold);
      const unlocked = total >= threshold;
      const shouldOpen = unlocked && !this.giftUnlocked && !this.cartHasGift && this.cartRewardsSubtotal < threshold;
      this.giftUnlocked = unlocked;
      if (!unlocked) this.selectedGift = null;
      this.giftPicker.hidden = !unlocked || this.cartHasGift || Boolean(this.selectedGift);
      if (shouldOpen) {
        customElements.whenDefined('quick-view-product').then(() => {
          if (this.giftUnlocked && !this.cartHasGift && !this.selectedGift) {
            this.giftPicker.querySelector('[data-bundle-free-gift]')?.click();
          }
        });
      }
    }

    selectBundleGift(variant) {
      if (!this.giftUnlocked || this.cartHasGift || !variant?.id || variant.available === false) return false;
      this.selectedGift = {
        id: variant.id,
        title: variant.public_title || variant.title,
        image: variant.featured_image?.src,
      };
      this.update();
      return true;
    }

    updateProgressHint(cartItems) {
      if (!cartItems || cartItems <= 0) {
        if (this.summaryCartHint) {
          this.summaryCartHint.hidden = true;
          this.summaryCartHint.textContent = '';
        }
        return;
      }

      const template = this.dataset.cartHintTemplate || 'You already have {count} bundle items in your cart';
      const text = template.replace('{count}', String(cartItems));

      if (this.summaryCartHint) {
        this.summaryCartHint.textContent = text;
        this.summaryCartHint.hidden = false;
      }
    }

    updateProductsClamp() {
      if (!this.productsWrap) return;

      if (window.innerWidth < 1024) {
        this.productsWrap.style.maxHeight = '';
        if (this.productsScroll) this.productsScroll.style.maxHeight = '';
        this.productsWrap.classList.remove('has-overflow');
        return;
      }

      const visibleCards = Array.from(this.querySelectorAll('[data-bundle-card], .bundle-builder__promo'))
        .filter((card) => !card.hidden && card.offsetParent !== null);

      if (visibleCards.length === 0) {
        this.productsWrap.style.maxHeight = '';
        if (this.productsScroll) this.productsScroll.style.maxHeight = '';
        this.productsWrap.classList.remove('has-overflow');
        return;
      }

      const rowTops = [];
      visibleCards.forEach((card) => {
        const top = Math.round(card.offsetTop);
        if (!rowTops.includes(top)) {
          rowTops.push(top);
        }
      });

      rowTops.sort((a, b) => a - b);

      if (rowTops.length <= 2) {
        this.productsWrap.style.maxHeight = '';
        if (this.productsScroll) this.productsScroll.style.maxHeight = '';
        this.productsWrap.classList.remove('has-overflow');
        return;
      }

      const secondRowTop = rowTops[1];
      let secondRowBottom = 0;

      visibleCards.forEach((card) => {
        if (Math.round(card.offsetTop) === secondRowTop) {
          secondRowBottom = Math.max(secondRowBottom, card.offsetTop + card.offsetHeight);
        }
      });

      if (secondRowBottom > 0) {
        this.productsWrap.style.maxHeight = `${secondRowBottom}px`;
        if (this.productsScroll) this.productsScroll.style.maxHeight = `${secondRowBottom}px`;
        this.productsWrap.classList.add('has-overflow');
      }
    }

    bumpElement(element, className) {
      if (!element) return;

      element.classList.remove(className);
      void element.offsetWidth;
      element.classList.add(className);

      window.setTimeout(() => {
        element.classList.remove(className);
      }, 380);
    }

    setSummaryProcessing(isProcessing) {
      this.isSummaryProcessing = isProcessing;
      this.summary?.classList.toggle('is-processing', isProcessing);
      this.summaryWrap?.classList.toggle('is-processing', isProcessing);
      this.summaryAnchor?.classList.toggle('is-processing', isProcessing);
      if (this.addToCartButton) {
        this.addToCartButton.disabled = isProcessing || this.getSelectedCards().length === 0;
      }
    }

    addBundleToCart() {
      const items = this.getSelectedCards().map(({ quantity, variant }) => ({
        id: variant.id,
        quantity,
        properties: {
          '_Source': 'Build Your Own Bundle',
        },
      }));

      if (items.length === 0) return;
      if (this.giftUnlocked && this.selectedGift && !this.cartHasGift) {
        items.push({
          id: this.selectedGift.id,
          quantity: 1,
          properties: { '_Free gift': 'Threshold 2' },
        });
      }

      this.addToCartButton.disabled = true;
      this.addToCartButton.classList.add('working');
      this.addBundleItems(items)
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.addToCartButton.disabled = false;
          this.addToCartButton.classList.remove('working');
        });
    }

    async removeCartItemFromCart(itemKey, button, feedbackId) {
      if (!itemKey) return;

      if (button) {
        button.disabled = true;
      }

      this.setSummaryProcessing(true);
      this.pendingCartRemovalKeys.add(itemKey);
      this.update();

      try {
        const response = await fetch(KROWN.settings.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: itemKey,
            quantity: 0,
          }),
        });
        const cart = await response.json();

        if (cart.status == 422) {
          throw new Error(cart.description || cart.message || 'Unable to remove cart item');
        }

        await this.refreshCartUi();
        this.applyCartEligibleItems(cart);
        this.pendingCartRemovalKeys.delete(itemKey);
        this.removeRemovedFeedback(feedbackId);
        this.update();
        this.setSummaryProcessing(false);
      } catch (error) {
        console.error(error);
        this.pendingCartRemovalKeys.delete(itemKey);
        this.removeRemovedFeedback(feedbackId);
        this.update();
        this.setSummaryProcessing(false);
        if (button) {
          button.disabled = false;
        }
      }
    }

    async refreshCartUi({ showCartSidebar = false } = {}) {
      const response = await fetch('?section_id=helper-cart');
      const text = await response.text();
      const cartSidebar = document.getElementById('site-cart-sidebar');

      if (!cartSidebar && showCartSidebar) {
        window.location.href = KROWN.settings.routes.cart_url;
        return;
      }

      const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
      const cartFormInnerHTML = sectionInnerHTML.getElementById('AjaxCartForm')?.innerHTML;
      const cartSubtotalInnerHTML = sectionInnerHTML.getElementById('AjaxCartSubtotal')?.innerHTML;
      const cartProgressBarInnerHTML = sectionInnerHTML.getElementById('AjaxCartProgressBar')?.innerHTML;
      const cartRecommendationsInnerHTML = sectionInnerHTML.getElementById('AjaxCartRecommendations')?.innerHTML;

      const cartItems = document.getElementById('AjaxCartForm');
      if (cartItems && cartFormInnerHTML) {
        cartItems.innerHTML = cartFormInnerHTML;
        if (typeof cartItems.ajaxifyCartItems === 'function') {
          cartItems.ajaxifyCartItems();
        }
      }

      document.querySelectorAll('[data-header-cart-count]').forEach((elm) => {
        const countSource = document.querySelector('#AjaxCartForm [data-cart-count]');
        if (countSource) {
          elm.innerHTML = countSource.innerHTML;
        }
      });

      if (document.getElementById('AjaxCartSubtotal') && cartSubtotalInnerHTML) {
        document.getElementById('AjaxCartSubtotal').innerHTML = cartSubtotalInnerHTML;
      }

      if (document.getElementById('AjaxCartProgressBar') && cartProgressBarInnerHTML !== undefined) {
        document.getElementById('AjaxCartProgressBar').innerHTML = cartProgressBarInnerHTML;
      }

      const recommendationsSidebar = document.getElementById('cart-recommendations-sidebar');
      if (recommendationsSidebar && typeof recommendationsSidebar.generateRecommendations === 'function') {
        recommendationsSidebar.generateRecommendations();
      } else if (document.getElementById('AjaxCartRecommendations') && cartRecommendationsInnerHTML) {
        document.getElementById('AjaxCartRecommendations').innerHTML = cartRecommendationsInnerHTML;
      }

      if (cartItems) {
        cartItems.dispatchEvent(new Event('cart-updated'));
        document.dispatchEvent(new CustomEvent('cart-updated', { detail: { source: 'build-your-own-bundle' } }));
      }

      if (showCartSidebar) {
        this.classList.remove('is-summary-open');
        cartSidebar?.show();
      }
    }

    async addBundleItems(items) {
      return fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
        body: JSON.stringify({ items }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status == 422) {
            throw new Error(response.description || 'Unable to add bundle to cart');
          }

          if (document.body.classList.contains('template-cart')) {
            window.location.href = KROWN.settings.routes.cart_url;
            return null;
          }

          return this.refreshCartUi({ showCartSidebar: true }).then(() => {
            this.clearKitSelections();
            this.refreshCartEligibleCount().then(() => this.update());
          });
        });
    }

    clearKitSelections() {
      this.selectedGift = null;
      this.cards.forEach((card) => {
        card._bundleSelections = {};
      });
    }

    getResizedImageSrc(src, size, crop = 'center') {
      const separator = src.includes('?') ? '&' : '?';
      let query = `${separator}width=${size}`;

      if (crop) {
        query += `&height=${size}&crop=${crop}`;
      }

      return `${src}${query}`.replace(/\n|\r|\s/g, '');
    }

    formatMoney(cents, format = window.KROWN?.settings?.shop_money_format || '${{amount}}') {
      if (typeof window.KROWN?.formatMoney === 'function') {
        return window.KROWN.formatMoney(cents, format);
      }

      return cents;
    }

    formatMoneyWithoutTrailingZeros(cents) {
      if (typeof window.KROWN?.formatMoneyWithoutTrailingZeros === 'function') {
        return window.KROWN.formatMoneyWithoutTrailingZeros(cents);
      }

      return this.formatMoney(cents).replace(/([.,]00)(?=\D*$)/, '');
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
}
